// D10/D11/D13/D14/D15 — digital commerce kits, libraries and planners.
import { useMemo, useState } from "react";
import { ResultRow, ToolSection, Note, Field, copyText } from "./ui";

function CopyButton({ getText, testid }) {
  const [state, setState] = useState("idle");
  return (
    <button type="button" data-testid={testid}
      onClick={async () => { setState((await copyText(getText())) ? "done" : "fail"); setTimeout(() => setState("idle"), 2500); }}
      className="text-sm font-medium underline underline-offset-4">
      {state === "done" ? "Copied" : state === "fail" ? "Select & copy manually" : "Copy output"}
    </button>
  );
}

/* ---------- D10 Commercial SEO Opportunity Mapper ---------- */
const D10_INTENTS = ["buy now", "compare", "how-to / problem", "cost / price", "near me / local"];
const D10_DEFAULT = [
  { query: "industrial RO plant price", intent: "cost / price", currentPage: "None — no page targets it", action: "" },
  { query: "RO plant vs water supplier cost comparison", intent: "compare", currentPage: "Blog post (buried, 2023)", action: "" },
  { query: "how to reduce water cost in hotel", intent: "how-to / problem", currentPage: "None", action: "" },
];
export function ToolD10() {
  const [rows, setRows] = useState(D10_DEFAULT);
  const setR = (i, k) => (e) => setRows((s) => s.map((x, xi) => (xi === i ? { ...x, [k]: e.target.value } : x)));
  const clusters = useMemo(() => {
    const byIntent = {};
    rows.forEach((r) => { if (r.query.trim()) { (byIntent[r.intent] = byIntent[r.intent] || []).push(r); } });
    return Object.entries(byIntent).map(([intent, items]) => ({ intent, items, uncovered: items.filter((i) => /none/i.test(i.currentPage)).length }));
  }, [rows]);
  return (
    <div data-testid="tool-D10">
      <p className="text-sm text-zinc-600 mb-5">Use your own data: Search Console queries, sales-call language, autocomplete. Map each real query to its buying intent and the page that should exist. No invented search volumes — your query list beats a generic tool's estimate.</p>
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="border border-zinc-200 p-4 grid md:grid-cols-12 gap-3" data-testid={`d10-row-${i}`}>
            <input value={r.query} onChange={setR(i, "query")} placeholder="Real query (from Search Console / sales calls)" className="md:col-span-4 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`d10-q-${i}`} />
            <select value={r.intent} onChange={setR(i, "intent")} className="md:col-span-2 border border-zinc-200 px-2 py-1.5 text-sm bg-white" data-testid={`d10-intent-${i}`}>
              {D10_INTENTS.map((x) => <option key={x}>{x}</option>)}
            </select>
            <input value={r.currentPage} onChange={setR(i, "currentPage")} placeholder="Current page (or 'None')" className="md:col-span-3 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`d10-page-${i}`} />
            <input value={r.action} onChange={setR(i, "action")} placeholder="Action: build / rewrite / link" className="md:col-span-3 border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-black" data-testid={`d10-action-${i}`} />
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setRows((s) => [...s, { query: "", intent: "buy now", currentPage: "", action: "" }])} className="mt-4 text-sm font-medium underline underline-offset-4" data-testid="d10-add">+ Add query</button>
      <ToolSection title="Opportunity map by intent" testid="d10-clusters">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clusters.map((c) => (
            <div key={c.intent} className={`border p-4 ${c.uncovered ? "border-red-300" : "border-zinc-200"}`} data-testid={`d10-cluster-${c.intent.replace(/[^a-z]/gi, "-")}`}>
              <div className="text-sm font-medium capitalize">{c.intent}</div>
              <div className="mt-1 text-xs text-zinc-500">{c.items.length} queries · {c.uncovered} with no page</div>
              <ul className="mt-2 space-y-1 text-xs text-zinc-600">
                {c.items.slice(0, 4).map((i, ix) => <li key={ix}>· {i.query}</li>)}
              </ul>
              {c.uncovered > 0 && <div className="mt-2 text-xs font-medium text-red-700">Build a dedicated page for this cluster</div>}
            </div>
          ))}
        </div>
        <Note>Rule of thumb: 'buy now' and 'compare' intents deserve landing pages with offers; 'how-to' intents deserve guides that introduce the offer. One intent, one page — mixing intents on one page ranks for neither.</Note>
      </ToolSection>
    </div>
  );
}

/* ---------- D11 AI Search Visibility Checker (evidence-bound, manual) ---------- */
const D11_PROMPTS = [
  "best [your category] consultant in [your city]",
  "top [your service] agencies in India",
  "who can help with [the problem you solve]",
  "[your category] consultant reviews",
  "alternatives to [best-known competitor]",
  "how much does [your service] cost in India",
  "[your service] for [a segment you serve]",
  "is [your brand] good / reliable",
];
export function ToolD11() {
  const [checks, setChecks] = useState({});
  const [notes, setNotes] = useState({});
  const engines = ["ChatGPT", "Gemini", "Perplexity"];
  const done = Object.values(checks).filter((v) => v !== undefined).length;
  const cited = Object.values(checks).filter((v) => v === "cited").length;
  const recommended = Object.values(checks).filter((v) => v === "recommended").length;
  const total = D11_PROMPTS.length;
  return (
    <div data-testid="tool-D11">
      <p className="text-sm text-zinc-600 mb-2">When buyers ask AI for a consultant, does it mention you? This is a manual, evidence-bound audit — we do not simulate AI answers. Run each prompt in each engine, in a logged-out/incognito session, and record what actually happens.</p>
      <Note tone="warn">AI answers vary by day, phrasing and account. Record the date and exact answer text in the notes — a finding without provenance is an anecdote.</Note>
      <div className="mt-5 space-y-4">
        {D11_PROMPTS.map((p, i) => (
          <div key={i} className="border border-zinc-200 p-4" data-testid={`d11-prompt-${i}`}>
            <div className="text-sm font-medium">"{p}"</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {[["recommended", "Recommended us unprompted"], ["cited", "Mentioned / cited"], ["absent", "Absent"], ["rival", "Rivals recommended instead"]].map(([val, label]) => (
                <button key={val} type="button" onClick={() => setChecks((s) => ({ ...s, [i]: val }))}
                  className={`px-3 py-1.5 text-xs border transition-colors ${checks[i] === val ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`d11-${i}-${val}`}>{label}</button>
              ))}
            </div>
            <input value={notes[i] || ""} onChange={(e) => setNotes((s) => ({ ...s, [i]: e.target.value }))} placeholder="Evidence: engine, date, exact wording…" className="mt-2 w-full border border-zinc-200 px-2 py-1.5 text-xs outline-none focus:border-black" data-testid={`d11-note-${i}`} />
          </div>
        ))}
      </div>
      <div className="mt-6 max-w-lg" data-testid="d11-result">
        <ResultRow label="Prompts checked" value={`${done} / ${total}`} />
        <ResultRow label="Recommended unprompted" value={String(recommended)} tone={recommended ? "good" : "bad"} strong />
        <ResultRow label="Mentioned or cited" value={String(cited)} />
        <ResultRow label="Honest verdict" value={done < total ? "Incomplete" : recommended >= 3 ? "Visible" : recommended + cited >= 3 ? "Present, not preferred" : "Invisible to AI buyers"} strong />
      </div>
      <ToolSection title="What moves AI visibility (honestly)">
        <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
          <li>Crawlable, specific service pages and FAQs — the exact text AI engines quote.</li>
          <li>Third-party citations: directories, reviews, guest articles, industry lists.</li>
          <li>Consistent naming: one brand name, one category description, everywhere.</li>
          <li>Re-run monthly with the same prompts; screenshot everything with dates.</li>
        </ul>
      </ToolSection>
    </div>
  );
}

/* ---------- D13 Festival Sale War-Room Planner ---------- */
const D13_PLAN = [
  ["T-21 to T-14", "Freeze SKUs, price floors and inventory commitments; book ad inventory", "Deals submitted; stock PO raised"],
  ["T-14 to T-7", "Listing audit on hero SKUs; creative live; coupons tested; OOS alerts armed", "All heroes ≥8/10 listing health"],
  ["T-7 to T-1", "War-room roster staffed; daily standup time set; dashboards tested with live data; escalation path written", "Dry run completed"],
  ["Sale days", "Hourly: price, Buy Box, OOS, ad pacing. Twice daily: contribution vs plan. Never change more than one lever per hour", "Actual vs plan logged daily"],
  ["T+1 to T+7", "Returns watch; review requests fired; the honest post-mortem while data is fresh", "Post-mortem minuted"],
];
export function ToolD13() {
  const [event, setEvent] = useState("Diwali sale");
  const [days, setDays] = useState([
    { day: "Day 1", planRevenue: 800000, actual: "" },
    { day: "Day 2", planRevenue: 600000, actual: "" },
    { day: "Day 3", planRevenue: 500000, actual: "" },
  ]);
  const planTotal = days.reduce((s, d) => s + (Number(d.planRevenue) || 0), 0);
  const actualTotal = days.reduce((s, d) => s + (Number(d.actual) || 0), 0);
  return (
    <div data-testid="tool-D13">
      <div className="max-w-sm"><Field label="Event name" type="text" value={event} onChange={setEvent} testid="d13-event" /></div>
      <ToolSection title="The three weeks before (won here, not on sale day)">
        <div className="space-y-3" data-testid="d13-plan">
          {D13_PLAN.map(([phase, action, kpi]) => (
            <div key={phase} className="border border-zinc-200 p-4 grid md:grid-cols-12 gap-2 items-start">
              <span className="md:col-span-2 text-xs font-semibold uppercase tracking-wide pt-1">{phase}</span>
              <span className="md:col-span-7 text-sm text-zinc-700 leading-relaxed">{action}</span>
              <span className="md:col-span-3 text-xs text-zinc-500">{kpi}</span>
            </div>
          ))}
        </div>
      </ToolSection>
      <ToolSection title={`${event} — actual vs plan dashboard`} testid="d13-dashboard">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[480px]">
            <thead><tr className="text-left text-xs uppercase tracking-widest text-zinc-500 border-b border-black">
              <th className="py-2 font-medium">Day</th><th className="py-2 font-medium">Plan (₹)</th><th className="py-2 font-medium">Actual (₹)</th><th className="py-2 font-medium">Attainment</th>
            </tr></thead>
            <tbody>
              {days.map((d, i) => {
                const att = d.actual && Number(d.planRevenue) > 0 ? Math.round((Number(d.actual) / Number(d.planRevenue)) * 100) : null;
                return (
                  <tr key={i} className="border-b border-black/10" data-testid={`d13-day-${i}`}>
                    <td className="py-2 pr-3 font-medium">{d.day}</td>
                    <td className="py-2 pr-3"><input type="number" value={d.planRevenue} onChange={(e) => setDays((s) => s.map((x, xi) => (xi === i ? { ...x, planRevenue: e.target.value } : x)))} className="w-28 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`d13-plan-${i}`} /></td>
                    <td className="py-2 pr-3"><input type="number" value={d.actual} onChange={(e) => setDays((s) => s.map((x, xi) => (xi === i ? { ...x, actual: e.target.value } : x)))} placeholder="fill on the day" className="w-28 border border-zinc-200 px-1.5 py-1 text-sm outline-none focus:border-black" data-testid={`d13-actual-${i}`} /></td>
                    <td className={`py-2 tabular-nums font-medium ${att !== null && att < 80 ? "text-red-700" : "text-emerald-700"}`} data-testid={`d13-att-${i}`}>{att === null ? "—" : `${att}%`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 max-w-md" data-testid="d13-result">
          <ResultRow label="Plan total" value={`₹${planTotal.toLocaleString("en-IN")}`} />
          <ResultRow label="Actual so far" value={`₹${actualTotal.toLocaleString("en-IN")}`} strong tone={actualTotal >= planTotal ? "good" : undefined} />
        </div>
      </ToolSection>
      <Note tone="warn">War-room rule: one lever per hour. Teams that change price, ads and coupons simultaneously learn nothing and burn all three.</Note>
    </div>
  );
}

/* ---------- D14 WhatsApp Lifecycle Library (11 authored, consent-first) ---------- */
const D14_LIBRARY = [
  { moment: "Welcome (post first order)", msg: "Hi {name}, your {brand} order is confirmed. Questions about anything? Reply here — a human answers. (Reply STOP anytime to opt out.)", note: "Set the tone: service channel first, marketing channel second." },
  { moment: "Delivery day", msg: "Your {product} arrives today, {name}. Quick start tip: {one_line_tip}. Anything off with the delivery? Tell us here.", note: "The highest-open message you'll ever send — use it for help, not upsell." },
  { moment: "Day 3 check-in", msg: "How's the {product} treating you, {name}? Reply 1 = loving it, 2 = stuck, 3 = issue. We read every reply.", note: "Catches problems before they become returns and reviews." },
  { moment: "Review request (day 10)", msg: "Two weeks in — would you mind an honest review? {link}. Two lines help the next buyer more than you think.", note: "Only ask after the check-in came back positive." },
  { moment: "Cross-sell (day 21)", msg: "{name}, customers who bought {product} often add {pair} for {reason}. Here's the honest comparison: {link}. No pressure either way.", note: "Recommend from basket data, not from stock you need to clear." },
  { moment: "Replenishment (consumables)", msg: "Running low on {product}, {name}? Reorder in one tap: {link}. (Usual run-out is around now — ignore if you've stocked up.)", note: "Time from actual consumption cycles, not from the calendar." },
  { moment: "Win-back (60 days silent)", msg: "We miss you is a bit much from a brand, {name} — so just this: {genuine_offer_or_news}. Good to have you back whenever.", note: "One message. If it doesn't land, wait a full cycle before the next." },
  { moment: "Festival (opted-in only)", msg: "Happy {festival}, {name}. If you're gifting this year, our {relevant_range} is at {offer} until {date}: {link}. Either way, have a good one.", note: "Festival blasts to non-engaged numbers are how you get blocked." },
  { moment: "Price-drop alert (wishlist)", msg: "You saved {product} a while back, {name} — it's now {price}. Here if you still want it: {link}", note: "Only for explicit wishlist/save actions. Surveillance-feeling messages burn trust." },
  { moment: "Feedback (post-support)", msg: "We closed your ticket about {issue}, {name}. Was it actually resolved? 1 = yes, 2 = not quite, 3 = call me.", note: "Closes the loop competitors leave open; escalates 2s and 3s to a human." },
  { moment: "VIP / loyalty", msg: "You're in our top customers, {name} — early access to {launch} before Thursday's release: {link}. Thanks for sticking with us.", note: "Exclusivity that isn't exclusive isn't loyalty. Keep the list genuinely small." },
];
export function ToolD14() {
  const [open, setOpen] = useState(null);
  const [copied, setCopied] = useState(null);
  return (
    <div data-testid="tool-D14">
      <Note tone="warn">Consent is the product: send only to numbers that opted in, identify yourself in the first line, honour STOP within 24 hours, and keep marketing under ~2 messages a week. WhatsApp punishes what customers punish.</Note>
      <div className="mt-5 space-y-2">
        {D14_LIBRARY.map((m, i) => (
          <div key={i} className="border border-zinc-200" data-testid={`d14-msg-${i}`}>
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 transition-colors" data-testid={`d14-toggle-${i}`}>
              <span className="text-sm font-medium">{i + 1}. {m.moment}</span>
            </button>
            {open === i && (
              <div className="border-t border-zinc-200 p-4 bg-zinc-50">
                <p className="text-sm text-zinc-800 leading-relaxed">{m.msg}</p>
                <p className="mt-2 text-xs text-zinc-500">{m.note}</p>
                <div className="mt-3">
                  <button type="button" data-testid={`d14-copy-${i}`}
                    onClick={async () => { setCopied((await copyText(m.msg)) ? i : -2); setTimeout(() => setCopied(null), 2500); }}
                    className="text-sm font-medium underline underline-offset-4">
                    {copied === i ? "Copied" : copied === -2 ? "Select & copy manually" : "Copy message"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <Note>Replace {'{variables}'} with real values and keep each message under 300 characters where possible. Measure replies and opt-outs per message — opt-out rate above 1% on any message is the market voting.</Note>
    </div>
  );
}

/* ---------- D15 Performance Agency Evaluation Kit ---------- */
const D15_QUESTIONS = [
  { q: "Show us contribution-level reporting from a current client — not ROAS screenshots.", looks: "P&L-level fluency; asks about your margins before promising multiples." },
  { q: "Which client fired you, and why?", looks: "A real story with a real lesson. 'We've never lost a client' is a red flag." },
  { q: "What would you NOT spend our money on?", looks: "Channel restraint. Agencies paid on spend have opinions worth testing." },
  { q: "How do you separate your effect from seasonality and our own promotions?", looks: "Holdouts, geo-splits, matched baselines — or at least honest uncertainty." },
  { q: "Who exactly works on our account, and how many accounts do they carry?", looks: "Names and a number. The pitch team is rarely the delivery team." },
  { q: "What happens in weeks 1–4, before 'the algorithm learns'?", looks: "Structure, tracking, creative testing plan — not vague ramp-up talk." },
  { q: "How is creative produced, tested and killed?", looks: "A cadence with kill criteria, not 'our designers are great'." },
  { q: "What do you need from us weekly to do your best work?", looks: "Specific asks: margins, stock, promo calendar, fast approvals." },
  { q: "Show a test you ran that failed and what changed after.", looks: "Failure literacy — the defining trait of honest performance teams." },
  { q: "How do you charge, and what would make you recommend we spend less?", looks: "Incentive awareness; a credible answer to the spend-less question." },
  { q: "What access do we keep — ad accounts, pixels, data?", looks: "'Your accounts, your data, full admin' — anything else is a hostage clause." },
  { q: "What will you tell us at month 3 if results are flat?", looks: "A diagnosis framework, not a promise. Guarantees are marketing, not planning." },
];
export function ToolD15() {
  const [scores, setScores] = useState({});
  const done = Object.keys(scores).length;
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  return (
    <div data-testid="tool-D15">
      <p className="text-sm text-zinc-600 mb-5">Twelve questions that separate agencies that report ROAS from those that build profit. Interview two or three agencies with the same script; score each answer 0–2 against the 'look for' notes.</p>
      <div className="space-y-3">
        {D15_QUESTIONS.map((item, i) => (
          <div key={i} className="border border-zinc-200 p-4" data-testid={`d15-q-${i}`}>
            <div className="text-sm font-medium">{i + 1}. {item.q}</div>
            <div className="text-xs text-zinc-500 mt-1">Look for: {item.looks}</div>
            <div className="mt-2 flex gap-1.5">
              {[0, 1, 2].map((v) => (
                <button key={v} type="button" onClick={() => setScores((s) => ({ ...s, [i]: v }))}
                  className={`w-8 h-8 text-xs border transition-colors ${scores[i] === v ? "border-black bg-black text-white" : "border-zinc-300 hover:border-black"}`}
                  data-testid={`d15-${i}-${v}`}>{v}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {done === D15_QUESTIONS.length && (
        <div className="mt-6 max-w-lg" data-testid="d15-result">
          <ResultRow label="Agency score" value={`${total} / 24`} strong tone={total >= 18 ? "good" : total >= 12 ? undefined : "bad"} />
          <ResultRow label="Verdict" value={total >= 18 ? "Strong — set the 30/60/90" : total >= 12 ? "Workable — close the gaps in the contract" : "Keep interviewing"} />
          <ToolSection title="The 30/60/90 scorecard to attach to the contract">
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-zinc-600">
              <li><strong>Day 30:</strong> tracking verified end-to-end; structure rebuilt; first creative tests live with kill dates.</li>
              <li><strong>Day 60:</strong> winning creative identified at spend level X; contribution-positive or a named, tested reason why not.</li>
              <li><strong>Day 90:</strong> contribution per order stable or improving at scaled spend; weekly reporting cadence running without chasing.</li>
            </ul>
          </ToolSection>
        </div>
      )}
    </div>
  );
}
